/**
 * Network Service - Handles all network-related operations
 * Following Single Responsibility Principle
 */

export const NetworkConnectionType = {
  GOOD: "good",
  POOR: "poor", 
  OFFLINE: "offline"
};

export const NetworkSpeedThresholds = {
  GOOD_SPEED: 2, // Mbps
  POOR_SPEED: 0.5 // Mbps
};

export const NetworkConfig = {
  PING_INTERVAL: 30000, // 30 seconds
  SPEED_TEST_INTERVAL: 60000, // 1 minute
  DEBOUNCE_DELAY: 1000, // 1 second
  CONNECTION_TIMEOUT: 5000, // 5 seconds
  PING_URLS: [
    "https://www.google.com/favicon.ico",
    "https://www.cloudflare.com/favicon.ico",
  ]
};

class NetworkService {
  constructor() {
    this.listeners = new Set();
    this.currentStatus = {
      isOnline: navigator.onLine,
      speed: null,
      connectionType: NetworkConnectionType.GOOD,
      latency: null,
      lastChecked: Date.now()
    };
    
    this.debounceTimer = null;
    this.pingInterval = null;
    this.speedTestInterval = null;
  }

  /**
   * Initialize the network service
   */
  initialize() {
    this.setupEventListeners();
    this.startPeriodicChecks();
    this.checkInitialConnection();
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.removeEventListeners();
    this.stopPeriodicChecks();
    this.listeners.clear();
  }

  /**
   * Subscribe to network status changes
   * @param {Function} callback - Callback function to receive status updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    
    // Immediately call with current status
    callback(this.currentStatus);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Get current network status
   * @returns {Object} Current network status
   */
  getCurrentStatus() {
    return { ...this.currentStatus };
  }

  /**
   * Setup event listeners for online/offline events
   */
  setupEventListeners() {
    window.addEventListener("online", this.handleOnlineEvent);
    window.addEventListener("offline", this.handleOfflineEvent);
    
    // Listen to Network Information API changes if available
    if ("connection" in navigator) {
      const connection = this.getConnection();
      if (connection) {
        connection.addEventListener("change", this.handleConnectionChange);
      }
    }
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    window.removeEventListener("online", this.handleOnlineEvent);
    window.removeEventListener("offline", this.handleOfflineEvent);
    
    if ("connection" in navigator) {
      const connection = this.getConnection();
      if (connection) {
        connection.removeEventListener("change", this.handleConnectionChange);
      }
    }
  }

  /**
   * Handle online event
   */
  handleOnlineEvent = () => {
    this.debouncedStatusUpdate(() => {
      this.checkConnection();
    });
  };

  /**
   * Handle offline event
   */
  handleOfflineEvent = () => {
    this.debouncedStatusUpdate(() => {
      this.updateStatus({
        isOnline: false,
        connectionType: NetworkConnectionType.OFFLINE,
        speed: null,
        latency: null
      });
    });
  };

  /**
   * Handle connection change event
   */
  handleConnectionChange = () => {
    this.debouncedStatusUpdate(() => {
      this.checkConnection();
    });
  };

  /**
   * Debounced status update to prevent rapid fire updates
   */
  debouncedStatusUpdate(callback) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    this.debounceTimer = setTimeout(callback, NetworkConfig.DEBOUNCE_DELAY);
  }

  /**
   * Start periodic network checks
   */
  startPeriodicChecks() {
    // Periodic connection ping
    this.pingInterval = setInterval(() => {
      this.checkConnection();
    }, NetworkConfig.PING_INTERVAL);

    // Periodic speed test (less frequent)
    this.speedTestInterval = setInterval(() => {
      if (this.currentStatus.isOnline) {
        this.measureNetworkSpeed();
      }
    }, NetworkConfig.SPEED_TEST_INTERVAL);
  }

  /**
   * Stop periodic checks
   */
  stopPeriodicChecks() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    if (this.speedTestInterval) {
      clearInterval(this.speedTestInterval);
      this.speedTestInterval = null;
    }
  }

  /**
   * Check initial connection on service startup
   */
  async checkInitialConnection() {
    await this.checkConnection();
    if (this.currentStatus.isOnline) {
      await this.measureNetworkSpeed();
    }
  }

  /**
   * Check internet connection
   */
  async checkConnection() {
    try {
      const startTime = performance.now();
      const isConnected = await this.pingServers();
      const endTime = performance.now();
      
      const latency = isConnected ? Math.round(endTime - startTime) : null;
      
      this.updateStatus({
        isOnline: isConnected,
        latency,
        connectionType: this.determineConnectionType(isConnected, this.currentStatus.speed, latency)
      });
      
    } catch (error) {
      console.warn("Network check failed:", error);
      this.updateStatus({
        isOnline: false,
        connectionType: NetworkConnectionType.OFFLINE,
        latency: null
      });
    }
  }

  /**
   * Ping multiple servers to check connectivity
   */
  async pingServers() {
    const promises = NetworkConfig.PING_URLS.map(url => this.pingUrl(url));
    
    try {
      // If any ping succeeds, consider connected
      const results = await Promise.allSettled(promises);
      return results.some(result => result.status === "fulfilled" && result.value === true);
    } catch (error) {
      return false;
    }
  }

  /**
   * Ping a single URL
   */
  async pingUrl(url) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), NetworkConfig.CONNECTION_TIMEOUT);

      await fetch(url, {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-cache",
        signal: controller.signal
      });

      clearTimeout(timeout);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Measure network speed using Network Information API or fallback method
   */
  async measureNetworkSpeed() {
    try {
      let speed = null;

      // Try Network Information API first
      const connection = this.getConnection();
      if (connection && typeof connection.downlink === "number") {
        speed = connection.downlink;
      } else {
        // Fallback: measure download speed
        speed = await this.measureDownloadSpeed();
      }

      this.updateStatus({
        speed,
        connectionType: this.determineConnectionType(this.currentStatus.isOnline, speed, this.currentStatus.latency)
      });

    } catch (error) {
      console.warn("Speed measurement failed:", error);
    }
  }

  /**
   * Measure download speed by downloading a small file
   */
  async measureDownloadSpeed() {
    try {
      const startTime = performance.now();
      const response = await fetch("https://httpbin.org/bytes/1024", {
        cache: "no-cache"
      });
      
      if (!response.ok) throw new Error("Speed test failed");
      
      await response.blob();
      const endTime = performance.now();
      
      const duration = (endTime - startTime) / 1000; // seconds
      const bitsLoaded = 1024 * 8; // 1KB in bits
      const speedBps = bitsLoaded / duration;
      const speedMbps = speedBps / (1024 * 1024);
      
      return Math.max(speedMbps, 0.1); // Minimum speed of 0.1 Mbps
    } catch (error) {
      return null;
    }
  }

  /**
   * Get connection object from Navigator
   */
  getConnection() {
    return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  }

  /**
   * Determine connection type based on various factors
   */
  determineConnectionType(isOnline, speed, latency) {
    if (!isOnline) {
      return NetworkConnectionType.OFFLINE;
    }

    // Check speed thresholds
    if (speed !== null) {
      if (speed >= NetworkSpeedThresholds.GOOD_SPEED) {
        return NetworkConnectionType.GOOD;
      } else if (speed >= NetworkSpeedThresholds.POOR_SPEED) {
        return NetworkConnectionType.POOR;
      } else {
        return NetworkConnectionType.POOR;
      }
    }

    // Check latency thresholds
    if (latency !== null) {
      if (latency > 1000) { // > 1 second
        return NetworkConnectionType.POOR;
      }
    }

    // Default to good if online but no specific metrics
    return NetworkConnectionType.GOOD;
  }

  /**
   * Update status and notify listeners
   */
  updateStatus(updates) {
    const previousStatus = { ...this.currentStatus };
    
    this.currentStatus = {
      ...this.currentStatus,
      ...updates,
      lastChecked: Date.now()
    };

    // Always notify listeners when lastChecked updates, or when status meaningfully changed
    const statusChanged = this.hasStatusChanged(previousStatus, this.currentStatus);
    const timestampChanged = previousStatus.lastChecked !== this.currentStatus.lastChecked;
    
    if (statusChanged || timestampChanged) {
      this.notifyListeners();
    }
  }

  /**
   * Check if status has meaningfully changed
   */
  hasStatusChanged(prev, current) {
    return (
      prev.isOnline !== current.isOnline ||
      prev.connectionType !== current.connectionType ||
      Math.abs((prev.speed || 0) - (current.speed || 0)) > 0.5 // Speed change > 0.5 Mbps
    );
  }

  /**
   * Notify all listeners of status change
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.currentStatus);
      } catch (error) {
        console.error("Error in network status listener:", error);
      }
    });
  }
}

// Create singleton instance
export const networkService = new NetworkService();

// Auto-initialize when module loads
if (typeof window !== "undefined") {
  networkService.initialize();
}

export default networkService;
